import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import { useContent } from '../../hooks/useContent';
import { useAuthContext } from '../../context/AuthContext';
import { storage } from '../../utils/storage';
import { Icon } from '../elements/icon.element.jsx';

// Nav labels are editable in Dictionary → Site content ("Header & footer").
export const CHROME_DEFAULTS = {
  HEADER: {
    WORKSPACE: 'Workspace',
    PROFILE: 'Profile',
    LOGIN: 'Log in',
    REGISTER: 'Sign up free'
  }
};

/**
 * The public navigation.
 *
 * WHY THIS DID NOT EXIST UNTIL NOW
 * -------------------------------
 * The header carried a logo and two auth buttons. Nothing else. So on all
 * twenty-nine public pages the only way to go anywhere was to scroll past the
 * whole page to the footer — including on the home page, where a first-time
 * visitor deciding whether this is for them had no way to reach the six pages
 * written to answer exactly that.
 *
 * Five destinations, not twenty-nine. The footer is the index; this is the
 * spine — what a stranger needs in order: what is it, is it for me, what does
 * it cost, who checks it, can I trust you.
 *
 * NO DROPDOWNS. A menu needs focus management, an escape key, click-outside,
 * and a mobile disclosure, and a half-built one is worse than none.
 *
 * On narrow screens the nav wraps to its own full-width row instead of being
 * squeezed between the logo and the auth buttons — at 647px it had 214px for
 * 451px of links, so two of the five were visible and nothing said the other
 * three existed. Given the whole width it usually fits; when it still does
 * not, it scrolls, with a fade at the right edge so that is visible rather
 * than something you find by accident. No JavaScript, keyboard order
 * unchanged, nothing to trap focus in.
 *
 * Overridable from Dictionary → Site content (chrome.HEADER_LINKS), and
 * navLinks.test.js fails on a target that does not resolve.
 */
const DEFAULT_NAV = [
  { to: '/product/how-it-works', label: 'How it works' },
  { to: '/solutions', label: 'Solutions' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/become-a-reviewer', label: 'For reviewers' },
  { to: '/trust', label: 'Trust' }
];

/**
 * The public nav's links: Dictionary → Site content → Header & footer
 * (chrome.HEADER_LINKS) when authored, DEFAULT_NAV while the dictionary is
 * unreachable. One resolver, so every header that shows the nav shows the
 * same one (GC-001: the home page rendered its own header without it).
 */
export const usePublicNavLinks = () => {
  const { content: chrome } = useContent('chrome', CHROME_DEFAULTS);
  // Dictionary-authored nav wins when it has entries, exactly the way the
  // footer resolves its columns — a lesson learned the expensive way, when a
  // coverage test checked a fallback nobody renders.
  const authored = Array.isArray(chrome?.HEADER_LINKS) ? chrome.HEADER_LINKS : [];
  return authored.length > 0
    ? authored
      .filter((item) => item?.label && (item.href || item.to))
      .map((item) => ({ to: item.href || item.to, label: item.label }))
    : DEFAULT_NAV;
};

/**
 * The nav row itself — rendered by PublicHeader and by the home page's own
 * header, which is hidden-behind by GuestLayout on "/" and so never showed
 * this row: the home page offered only Log in and Sign up, and an expert
 * looking for "Review for Moksy" had to find the footer (GC-001).
 */
export const PublicNav = ({ className = '' }) => {
  const navLinks = usePublicNavLinks();
  return (
    <nav aria-label="Site" className={`order-last flex w-full min-w-0 items-center gap-1 md:order-none md:w-auto md:flex-1 ${className}`}>
      <div className="relative min-w-0 flex-1">
        {/* Marks the right edge as scrollable when the row overflows. Sits
            above the list and ignores pointer events so it cannot eat a tap
            on the last link.

          Shown at EVERY width, not only below md: the row overflows on a
          tablet too, and an affordance that disappears at exactly the
          width where the links stop fitting is the opposite of its job.

          RF-010: this faded to `--moksy-canvas` (#fafafb), which is
          indistinguishable from the white/near-white background every
          public page actually renders behind the header — so the "hint"
          was there in the DOM but invisible to a real visitor, which a
          walk correctly read as no hint at all. A grey tint shows up as a
          visible edge shadow against any of those backgrounds, which
          matching the canvas colour exactly never reliably does. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-gray-300/70 to-transparent"
      />
      {/* ⚠️ Centred with AUTO MARGINS on the end items, never
          `justify-center`.

            A centred flex row that outgrows its scroll container overflows
            at BOTH ends, and the left overflow cannot be scrolled to — so
            at around 800px, where the five links no longer fit between the
            logo and the auth buttons, "How it works" was cut in half and
            unreachable (GC-087). Auto margins centre the group while there
            is slack and collapse to zero once there is not, which leaves
            every link reachable at every width. */}
        <ul className="flex items-center gap-1 overflow-x-auto md:gap-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {navLinks.map((item) => (
            <li key={item.to} className="md:first:ml-auto md:last:mr-auto">
              <Link
                to={item.to}
                className="block whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-neutral"
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      {/* RF-014: site search (JRN-A02) had no entry point anywhere a visitor
          could reach — not the header, not Help, not the home page. This is
          shared by PublicNav's two callers (PublicHeader and the home page's
          own header, Dashboard.jsx) — the same GC-001 lesson: one component,
          not two copies that drift. Outside the scrolling <ul> so it is
          never the thing that scrolls out of reach. */}
      <Link
        to="/search"
        aria-label="Search Moksy"
        title="Search"
        className="flex shrink-0 items-center justify-center rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-neutral md:order-last"
      >
        <Icon name="search" className="h-4.5 w-4.5" />
      </Link>
    </nav>
  );
};

export const PublicHeader = () => {
  const brand = useLanguage('branding');
  const { t } = useContent('chrome', CHROME_DEFAULTS);
  const { isAuthenticated, loading: authLoading } = useAuthContext();
  const brandName = brand?.META?.NAME || brand?.NAME || 'Moksy';
  const brandLogo = brand?.LOGO?.PRIMARY || brand?.LOGO?.SECONDARY || '';
  // Same guard as Dashboard.jsx's internal strip — don't flash auth
  // buttons while /me is still resolving. If a token is in storage
  // but hydration hasn't completed (or failed), offer Workspace as
  // an escape hatch rather than misleading the user with "Log in".
  const tokenAvailable = typeof storage.hasUsableAccessToken === 'function' && storage.hasUsableAccessToken();


  // Open, in-flow header — mirrors the landing page's header (Dashboard.jsx)
  // so every public page shares that airy, borderless look: brand left, quiet
  // auth pills right, no sticky bar. Width + padding line up with GuestLayout's
  // content wrapper so the brand mark sits flush with the page below it.
  return (
    <header className="w-full">
      <div className="mx-auto flex w-full max-w-[1600px] flex-wrap items-center justify-between gap-x-4 gap-y-3 px-4 py-5 md:flex-nowrap md:gap-8 md:px-6 lg:px-8">
        <Link to="/" className="shrink-0 text-base font-semibold tracking-tight text-neutral">
          {brandLogo ? (
            <img src={brandLogo} alt={brandName} className="h-8 w-auto" />
          ) : (
            brandName
          )}
        </Link>

        <PublicNav />

        {authLoading ? (
          <div className="flex shrink-0 items-center gap-3">
            <span className="h-9 w-24 rounded-full bg-gray-100" aria-hidden="true" />
          </div>
        ) : isAuthenticated ? (
          <div className="flex shrink-0 items-center gap-3">
            <Link to="/home" className="rounded-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">
              {t('HEADER.WORKSPACE')}
            </Link>
            <Link to="/profile" className="rounded-full border border-gray-300 bg-background px-4 py-2 text-sm font-medium text-gray-800 hover:border-gray-400 hover:bg-gray-50">
              {t('HEADER.PROFILE')}
            </Link>
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-3">
            {tokenAvailable ? (
              <Link to="/home" className="rounded-full bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black">
                {t('HEADER.WORKSPACE')}
              </Link>
            ) : null}
            <Link to="/login" className="rounded-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">
              {t('HEADER.LOGIN')}
            </Link>
            <Link to="/register" className="rounded-full border border-gray-300 bg-background px-4 py-2 text-sm font-medium text-gray-800 hover:border-gray-400 hover:bg-gray-50">
              {t('HEADER.REGISTER')}
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};
